/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  KEY_SPACE_COMMAND,
  TextNode,
} from "lexical";
import * as ReactDOM from "react-dom";

import { Dialog, DialogTrigger } from "@/components/Elements";
import { $createCodeNode } from "@lexical/code";
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import { ImageIcon, ListBulletIcon, TableIcon } from "@radix-ui/react-icons";
import { ListChecks, TextIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  BsCode,
  BsQuote,
  BsTypeH1,
  BsTypeH2,
  BsTypeH3,
  BsTypeH4,
  BsTypeH5,
  BsTypeH6,
} from "react-icons/bs";
import { TbListNumbers, TbSparkles } from "react-icons/tb";
import useModal from "../../hooks/useModal";
import { InsertImageDialog } from "../ImagesPlugin";

export class ComponentPickerOption extends MenuOption {
  // What shows up in the editor
  title: string;
  // Icon for display
  icon?: JSX.Element;
  // For extra searching.
  keywords: Array<string>;
  // TBD
  keyboardShortcut?: string;
  // What happens when you select this option?
  onSelect: (queryString: string) => void;
  // Is this a dialog?
  isDialog?: boolean;

  constructor(
    title: string,
    options: {
      icon?: JSX.Element;
      keywords?: Array<string>;
      keyboardShortcut?: string;
      onSelect: (queryString: string) => void;
      isDialog?: boolean;
    }
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords || [];
    this.icon = options.icon;
    this.keyboardShortcut = options.keyboardShortcut;
    this.onSelect = options.onSelect.bind(this);
    this.isDialog = options.isDialog;
  }
}

export function ComponentPickerMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
  setLastSelection,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  option: ComponentPickerOption;
  setLastSelection?: (selection: any) => void;
}) {
  let className = "item";
  if (isSelected) {
    className += " selected";
  }

  if (option.isDialog) {
    return (
      <DialogTrigger
        key={option.key}
        className="cursor-default space-x-2 select-none aria-selected:bg-zinc-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:aria-selected:bg-zinc-700 group flex w-full items-center rounded-md px-1 py-1 text-xs text-zinc-800 dark:text-zinc-100 focs:outline-none outline-none focus:[&:not(:focus-visible)]:outline-none focus:outline-none focus:ring-transparent border-transparent focus:border-transparent focus:ring-0 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        ref={option.setRefElement}
        aria-selected={isSelected}
        id={"typeahead-item-" + index}
        onMouseEnter={onMouseEnter}
        onClick={(event) => {
          onClick();
        }}
      >
        {option.icon}
        <span className="text">{option.title}</span>
      </DialogTrigger>
    );
  }

  return (
    <button
      key={option.key}
      tabIndex={-1}
      className="cursor-pointer space-x-2 font-base select-none aria-selected:bg-zinc-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:aria-selected:bg-zinc-700 group flex w-full items-center rounded-md px-1 py-1 text-xs text-zinc-800 dark:text-zinc-100 focs:outline-none outline-none focus:[&:not(:focus-visible)]:outline-none focus:outline-none focus:ring-transparent border-transparent focus:border-transparent focus:ring-0 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={"typeahead-item-" + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {option.icon}
      <span className="text">{option.title}</span>
    </button>
  );
}

const IconWrapper = ({ children }) => (
  <div className="bg-zinc-100 border dark:bg-zinc-900 dark:border-zinc-800 rounded-md p-1.5">
    {children}
  </div>
);

export const FormattingIconMap = {
  AiWriter: (
    <IconWrapper>
      <TbSparkles
        style={{ fill: "#059669", strokeColor: "#059669" }}
        className="text-emerald-600 w-3.5 h-3.5 flex-shrink-0"
      />
    </IconWrapper>
  ),
  Text: (
    <IconWrapper>
      <TextIcon className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 1": (
    <IconWrapper>
      <BsTypeH1 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 2": (
    <IconWrapper>
      <BsTypeH2 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 3": (
    <IconWrapper>
      <BsTypeH3 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 4": (
    <IconWrapper>
      <BsTypeH4 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 5": (
    <IconWrapper>
      <BsTypeH5 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Heading 6": (
    <IconWrapper>
      <BsTypeH6 className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Numbered List": (
    <IconWrapper>
      <TbListNumbers className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Bulleted List": (
    <IconWrapper>
      <ListBulletIcon className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Check List": (
    <IconWrapper>
      <ListChecks className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  "Code Block": (
    <IconWrapper>
      <BsCode className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  Quote: (
    <IconWrapper>
      <BsQuote className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  Image: (
    <IconWrapper>
      <ImageIcon className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
  Table: (
    <IconWrapper>
      <TableIcon className="w-3.5 h-3.5" />
    </IconWrapper>
  ),
};

export default function ComponentPickerMenuPlugin({
  setLastSelection,
  showWriteWithAi,
}: {
  setLastSelection: (selection: any) => void;
  showWriteWithAi: boolean;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [modal, showModal] = useModal();

  const checkForTriggerMatch = useBasicTypeaheadTriggerMatch("/", {
    minLength: 0,
  });

  const getDynamicOptions = useCallback(() => {
    const options: Array<ComponentPickerOption> = [];

    if (queryString == null) {
      return options;
    }

    const fullTableRegex = new RegExp(/^([1-9]|10)x([1-9]|10)$/);
    const partialTableRegex = new RegExp(/^([1-9]|10)x?$/);

    const fullTableMatch = fullTableRegex.exec(queryString);
    const partialTableMatch = partialTableRegex.exec(queryString);

    if (fullTableMatch) {
      const [rows, columns] = fullTableMatch[0]
        .split("x")
        .map((n: string) => parseInt(n, 10));

      options.push(
        new ComponentPickerOption(`${rows}x${columns} Table`, {
          icon: <i className="icon table" />,
          keywords: ["table"],
          onSelect: () =>
            // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
            editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows }),
        })
      );
    } else if (partialTableMatch) {
      const rows = parseInt(partialTableMatch[0], 10);

      options.push(
        ...Array.from({ length: 5 }, (_, i) => i + 1).map(
          (columns) =>
            new ComponentPickerOption(`${rows}x${columns} Table`, {
              icon: <i className="icon table" />,
              keywords: ["table"],
              onSelect: () =>
                // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
                editor.dispatchCommand(INSERT_TABLE_COMMAND, { columns, rows }),
            })
        )
      );
    }

    return options;
  }, [editor, queryString]);

  const options = useMemo(() => {
    const baseOptions = [
      showWriteWithAi
        ? new ComponentPickerOption("Write with AI...", {
            icon: FormattingIconMap.AiWriter,
            keywords: ["ai", "aiwriter", "ai writer", "ai-writer", "ai writer"],
            onSelect: () => {
              editor.update(() => {
                const selection = $getSelection();
                if (selection && $isRangeSelection(selection)) {
                  setLastSelection(selection);
                }

                setTimeout(() => {
                  editor.focus();
                  editor.dispatchCommand(KEY_SPACE_COMMAND, undefined);
                }, 0);
              });
            },
          })
        : null,
      new ComponentPickerOption("Text", {
        icon: FormattingIconMap.Text,
        keywords: ["normal", "paragraph", "p", "text"],
        onSelect: () =>
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createParagraphNode());
            }
          }),
      }),
      ...Array.from({ length: 4 }, (_, i) => i + 1).map(
        (n) =>
          new ComponentPickerOption(`Heading ${n}`, {
            icon: FormattingIconMap[`Heading ${n}`],
            keywords: ["heading", "header", `h${n}`],
            onSelect: () =>
              editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                  $setBlocksType(selection, () =>
                    // @ts-ignore Correct types, but since they're dynamic TS doesn't like it.
                    $createHeadingNode(`h${n}`)
                  );
                }
              }),
          })
      ),
      new ComponentPickerOption("Quote", {
        icon: FormattingIconMap.Quote,
        keywords: ["block quote"],
        onSelect: () =>
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createQuoteNode());
            }
          }),
      }),
      new ComponentPickerOption("Numbered List", {
        icon: FormattingIconMap["Numbered List"],
        keywords: ["numbered list", "ordered list", "ol"],
        onSelect: () =>
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
      }),
      new ComponentPickerOption("Bulleted List", {
        icon: FormattingIconMap["Bulleted List"],
        keywords: ["bulleted list", "unordered list", "ul"],
        onSelect: () =>
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
      }),
      new ComponentPickerOption("Check List", {
        icon: FormattingIconMap["Check List"],
        keywords: ["check list", "todo list"],
        onSelect: () =>
          editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
      }),
      new ComponentPickerOption("Table", {
        icon: FormattingIconMap.Table,
        keywords: ["table", "grid", "spreadsheet", "rows", "columns"],
        onSelect: () =>
          editor.dispatchCommand(INSERT_TABLE_COMMAND, {
            columns: "3",
            rows: "3",
            includeHeaders: false,
          }),
      }),
      new ComponentPickerOption("Image", {
        icon: FormattingIconMap.Image,
        keywords: ["image", "photo", "picture", "file"],
        onSelect: () =>
          showModal("Insert Image", (onClose) => (
            <InsertImageDialog activeEditor={editor} onClose={onClose} />
          )),
        isDialog: true,
      }),
      new ComponentPickerOption("Code Block", {
        icon: FormattingIconMap["Code Block"],
        keywords: ["code", "code block", "preformatted"],
        onSelect: () =>
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              if (selection.isCollapsed()) {
                $setBlocksType(selection, () => $createCodeNode());
              } else {
                const textContent = selection.getTextContent();
                const codeNode = $createCodeNode();
                selection.insertNodes([codeNode]);
                selection.insertRawText(textContent);
              }
            }
          }),
      }),
    ].filter(Boolean);

    const dynamicOptions = getDynamicOptions();

    return queryString
      ? [
          ...dynamicOptions,
          ...baseOptions.filter((option) => {
            return new RegExp(queryString, "gi").exec(option.title) ||
              option.keywords != null
              ? option.keywords.some((keyword) =>
                  new RegExp(queryString, "gi").exec(keyword)
                )
              : false;
          }),
        ]
      : baseOptions;
  }, [editor, getDynamicOptions, queryString, showModal]);

  const onSelectOption = useCallback(
    (
      selectedOption: ComponentPickerOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
      matchingString: string
    ) => {
      if (selectedOption.isDialog) {
        setDialogOpen(true);
      }

      editor.update(() => {
        if (nodeToRemove) {
          nodeToRemove.remove();
        }
        selectedOption.onSelect(matchingString);
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open);
      }}
    >
      {modal}
      <LexicalTypeaheadMenuPlugin<ComponentPickerOption>
        onQueryChange={setQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForTriggerMatch}
        options={options}
        menuRenderFn={(
          anchorElementRef,
          { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
        ) =>
          anchorElementRef.current && options.length
            ? ReactDOM.createPortal(
                <div className="overflow-hidden component-picker-menu z-50 origin-top-right divide-y divide-zinc-100 dark:divide-zinc-600 rounded-md backdrop-blur-lg bg-white/90 dark:bg-zinc-900/50 shadow-lg ring-1 ring-zinc-900 dark:ring-1 dark:ring-zinc-50 dark:ring-opacity-5 ring-opacity-5 focus:outline-none">
                  <ul className="overflow-auto max-h-80 py-1 px-1 text-zinc-700 dark:text-zinc-400 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-zinc-900 [&_[cmdk-group-heading]]:dark:text-zinc-300 focus:[&:not(:focus-visible)]:outline-none focus:outline-none focus:ring-transparent aria-selected:ring-transparent aria-selected:outline-none">
                    {options.map((option, i: number) => (
                      <ComponentPickerMenuItem
                        index={i}
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i);
                          selectOptionAndCleanUp(option);
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i);
                        }}
                        key={option.key}
                        option={option}
                      />
                    ))}
                  </ul>
                </div>,
                anchorElementRef.current
              )
            : null
        }
      />
    </Dialog>
  );
}
